import { BaseRange, Editor, Range, Node, Element, Path, Point, Transforms } from 'slate';
import { CustomEditor, SlateStateActions } from '../../../../models/slate-editor';
import { currentWord, getBlockWords } from './currentWord';
import { PostEditorTextUpdateAction } from '../../states/PostEditorActions';
import { PostEditorLinkToCustomLink, hasLinksChecker, insertLink, linkMatcher, LinkType } from './withLinks';
import { SocialProfileWithBoardsAndMentionType } from '../../helpers/mapSelectedProfilesValues';
import { showToast } from '../../../../helpers/showToast';
import { PostEditorLink } from '../../../../models/post-editor';
import { makeFinalLink } from '../../states/reducers/link.reducer';
import { MutableRefObject } from 'react';

type LinkRange = {
  link: LinkType;
  range: BaseRange;
};

export type BaseLinkSettings = Omit<PostEditorLink, 'initialUrl' | 'url'>;

export const onChange =
  (
    editor: CustomEditor
  ): ((
    actions: SlateStateActions,
    payload: PostEditorTextUpdateAction['payload'],
    profile: SocialProfileWithBoardsAndMentionType | null,
    linkSettings: BaseLinkSettings,
    direction: null | 'left',
    initialLink: string | null,
    pastedData: string | undefined,
    previousUndoLengthRef: MutableRefObject<number>
  ) => void) =>
  (actions, payload, profile, linkSettings, direction, initialLink, pastedData, previousUndoLengthRef): void => {
    const { selection } = editor;

    const { setIndex, setSearch, setTarget, setTransformType, updateText } = actions;
    const hasLocalLinks = hasLinksChecker(editor.children);
    const hasLinks = hasLocalLinks || (initialLink ? PostEditorLinkToCustomLink(initialLink, linkSettings) : false);
    // ^ we need to check internal links (editor local state) too, since we may delete links from one variation
    // totally and move focus to another variation. state might be offsync yet, and it would result in
    // detecting all possible links in focused editor, since it compare links against global (not yet updated) state. this fix prevents it
    const currentUndoLength = editor.history.undos.length;

    const shouldMention =
      profile?.mentionType === 'FACEBOOK_PAGE' ||
      profile?.mentionType === 'LINKEDIN_COMPANY' ||
      profile?.mentionType === 'TWITTER_USER';

    const shouldRemoveLink = profile?.mentionType === 'TWITTER_USER';

    if (selection && Range.isCollapsed(selection)) {
      const [start, end] = Range.edges(selection);
      if (Point.equals(start, end) && start.offset === 0 && Path.hasPrevious(start.path) && direction === 'left') {
        const before = Path.previous(start.path);
        const node = Node.get(editor, before);
        if (Element.isElement(node) && node.type === 'link') {
          Transforms.select(editor, before);
        }
        return;
      }
      const before = currentWord(editor, selection, {
        terminator: ['@'],
        directions: 'left',
        include: true
      });
      const beforeRange = before && Editor.range(editor, before, start);
      const beforeText = beforeRange && Editor.string(editor, beforeRange);
      const beforeMentionMatch = beforeText && beforeText.match(/^@([\w\s]+)$/);
      const beforeSelection = Editor.before(editor, selection.anchor, { unit: 'character' });
      const selectionRange = beforeSelection && Editor.range(editor, beforeSelection, selection.focus);
      const currentText = selectionRange && Editor.string(editor, selectionRange);

      if (beforeMentionMatch && beforeMentionMatch[1].length > 1 && shouldMention) {
        setTransformType('mention');
        setTarget(beforeRange);
        setSearch(beforeMentionMatch[1]);
        setIndex(0);
        return;
      } else if (
        profile === null &&
        currentText === '@' &&
        typeof payload.customizeIndex !== 'number' &&
        payload.customizeContentPerProfile
      ) {
        // we have multiple profiles, and we're not in customize per profile mode
        showToast({
          severity: 'info',
          summary: 'Adding a mention',
          detail: 'To add mentions, you need to customize the content for each profile.'
        });
        return;
      } else if (profile && currentText === '@' && !shouldMention) {
        // we have profile, but we can't add mention
        if (profile.__typename === 'InstagramSocialProfile') {
          showToast({
            severity: 'info',
            summary: 'Adding a mention',
            detail: 'Enter the exact username.'
          });
        } else if (profile.__typename === 'FacebookSocialProfile') {
          showToast({
            severity: 'warn',
            summary: 'Adding a mention',
            detail: "Tags can't be added for post to a group"
          });
        }
        return;
      } else {
        if (pastedData && !initialLink && hasLocalLinks) {
          const { children: _, type: __, ...postEditorLink } = hasLocalLinks;
          payload.link = postEditorLink;
          // if we have pasted data, it means we already have links constructed there. we just need to make sure we
          // propagate changes to the post editor
        } else if (currentUndoLength < previousUndoLengthRef.current && !initialLink && hasLocalLinks) {
          // we reverted slate state where we had links but we don't have them now detected in the editor, which means
          // we need to dispatch an action to update post editor state with restored link

          const { children: _, type: __, ...postEditorLink } = hasLocalLinks;
          payload.link = postEditorLink;
          // Access the content after undo
        } else {
          const ranges = getBlockWords(editor);
          const linkMatch: LinkRange[] = ranges.flatMap(range => {
            const checkString = Editor.string(editor, range);
            const link = linkMatcher(checkString);
            return link ? { link, range } : [];
          });

          const checkLinkRanges = linkMatch.reduce((prev, current) => {
            if (prev) return prev;
            return Range.includes(current.range, selection);
          }, false);

          const firstTimeDetection = !checkLinkRanges && hasLinks === false;
          const alreadyHaveLinks = !checkLinkRanges;

          // no link present in the text editor, nor the state, meaning we are detecting it for the first time
          if (linkMatch.length && firstTimeDetection) {
            const url = makeFinalLink({ ...linkSettings, initialUrl: linkMatch[0].link.text });
            payload.link = {
              ...linkSettings,
              initialUrl: linkMatch[0].link.text,
              url
            };
            payload = insertLink(editor, linkMatch[0].range, payload);
            // we detected a link, but we already have links. so we should check if the newly detected link is the same as the prev one, and if it is, we'll insert this one in th eeditor too
          } else if (linkMatch.length && alreadyHaveLinks && hasLinks) {
            const shouldInsert = linkMatch.reduce<LinkRange | false>((prev, current) => {
              if (current.link.url === hasLinks.url || current.link.text === hasLinks.initialUrl) {
                return current;
              }
              return prev;
            }, false);
            if (shouldInsert) {
              // we just need to insert the link we don't need to update the link state since we already have the same link in the state
              insertLink(editor, shouldInsert.range, { ...payload, link: hasLinks });
            }
          } else if (!hasLocalLinks && hasLinks && shouldRemoveLink) {
            // if we are on tw, and we delete the link we should remove it from global state too
            // TODO should we even do this ? Can't we just rely on the validation
          } else {
            // TODO nothing, we'll update it from the modal since now we have access to the editor
            //none of those, it means we have to check for the existing links
            // const getExistingLinks = Node.elements(editor, {
            //   pass: node => Element.isElement(node[0]) && node[0].type === 'link'
            // });
            //
            // let linkEntryNodes: [PostEditorLink, Path][] = [];
            // for (const [node, path] of Array.from(getExistingLinks)) {
            //   if (node.type === 'link' && Element.isElement(node)) {
            //     const { type: _type, children: _children, ...linkFromNode } = node;
            //     linkEntryNodes = [...linkEntryNodes, [linkFromNode, path]];
            //   }
            // }
            // if (linkEntryNodes.length) {
            //   // we make sure we pass the linkEntryNode as a link to the textUpdate
            //   payload.link = linkEntryNodes[0][0];
            // }
          }
        }
      }
    }

    previousUndoLengthRef.current = currentUndoLength;
    setTarget(null);
    setTransformType(null);
    updateText(payload);
  };
